import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowRight, Clock, Download, FileText, Receipt, Wallet } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Card } from '@shared/ui/containers/Card';
import { formatDate, formatMoney } from '@shared/lib/format';
import { cn } from '@shared/lib/cn';
import {
  fetchBillingPage,
  fetchStatements,
  fetchStatementDownloadUrl,
  fetchAgeingSummary,
  fetchPaymentAvailability,
  payAccountBalance,
  type BillingAccount,
  type BillingTransaction,
  type BillingTransactionType,
  type MemberStatement,
} from './api';
import { pageCopy } from '@shared/lib/pageCopy';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { useModuleStore } from '@shared/stores/moduleStore';
import { saveBlobAsDownload } from '@features/pdf-viewer/PdfViewerPage';
import { platform } from '@shared/lib/platform';
import { Browser } from '@capacitor/browser';

const TABS: Array<{ key: BillingTransactionType; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'invoice', label: 'Invoices' },
  { key: 'receipt', label: 'Payments' },
  { key: 'creditnote', label: 'Credit notes' },
];

const TYPE_LABEL: Record<string, string> = {
  invoice: 'Invoice',
  receipt: 'Payment',
  creditnote: 'Credit note',
  refund: 'Refund',
  journal: 'Journal',
};

const STATUS_TONE: Record<string, string> = {
  approved: 'bg-success/10 text-success',
  paid: 'bg-success/10 text-success',
  complete: 'bg-success/10 text-success',
  pending: 'bg-warning/10 text-warning',
  draft: 'bg-sunken text-ink-secondary',
  void: 'bg-ink-muted/10 text-ink-muted',
  voided: 'bg-ink-muted/10 text-ink-muted',
  cancelled: 'bg-ink-muted/10 text-ink-muted',
};

function money(amount: number, currencyCode: string): string {
  return formatMoney(amount, { currency: currencyCode });
}

function docLabel(tx: BillingTransaction): string {
  return (
    tx.document_number ||
    tx.reference_number ||
    `${TYPE_LABEL[tx.transaction_type] ?? tx.transaction_type}-${tx.transaction_id.slice(-6).toUpperCase()}`
  );
}

/**
 * Financial account — estate header, balance in account currency, and
 * filterable invoice / payment / credit-note history.
 */
export function Invoices(): JSX.Element {
  const copy = pageCopy().invoices;
  const [activeTab, setActiveTab] = useState<BillingTransactionType>('all');
  const statementsEnabled = useModuleStore((s) => s.isModuleEnabled('financials:statements'));
  const payQuery = useQuery({ queryKey: ['payment-availability'], queryFn: fetchPaymentAvailability });

  const billingQuery = useQuery({
    queryKey: ['billing', activeTab],
    queryFn: () => fetchBillingPage({ type: activeTab, limit: 50 }),
    // Keep the previous tab's data on screen while the new filter loads so
    // the balance card + tabs stay mounted — otherwise the full-page skeleton
    // collapses the layout and the scroll position jumps back to the top.
    placeholderData: keepPreviousData,
  });

  const account = billingQuery.data?.account;
  const transactions = billingQuery.data?.transactions ?? [];

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader collapsible eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} />

      <div className="px-5 pt-5 pb-safe-or-6">
        {billingQuery.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-10 rounded-pill" />
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
          </div>
        ) : billingQuery.isError ? (
          <ErrorState onRetry={() => billingQuery.refetch()} />
        ) : !account ? (
          <EmptyState
            icon={<Receipt className="h-7 w-7" strokeWidth={1.5} />}
            title="No billing account."
            description="Your profile is not linked to a financial account yet."
          />
        ) : (
          <>
            {/* "What you owe now": balance + its ageing breakdown, grouped at the top. */}
            <BalanceCard account={account} canPay={payQuery.data === true} />

            {statementsEnabled ? (
              <AgeingSummaryCard currency={account.currency_code} />
            ) : null}

            {/* Primary content: the transaction ledger. */}
            <div
              className="mt-5 flex gap-2 overflow-x-auto pb-1 scrollbar-none"
              role="tablist"
              aria-label="Transaction type"
            >
              {TABS.map((tab) => {
                const on = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      'shrink-0 rounded-pill px-4 py-2 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] transition-colors',
                      on
                        ? 'bg-primary-900 text-white'
                        : 'bg-card hairline text-ink-secondary',
                    )}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {transactions.length === 0 ? (
              <EmptyState
                className="mt-6"
                icon={<Wallet className="h-7 w-7" strokeWidth={1.5} />}
                title={emptyTitle(activeTab)}
                description="Transactions will appear here once they are posted to your account."
              />
            ) : (
              <div className="mt-4 flex flex-col gap-3">
                {transactions.map((tx) => (
                  <TransactionCard key={tx.transaction_id} tx={tx} accountCurrency={account.currency_code} />
                ))}
              </div>
            )}

            {/* Documents: formal statements, least-used — kept at the bottom. */}
            {statementsEnabled ? <StatementsCard currency={account.currency_code} /> : null}
          </>
        )}
      </div>
    </div>
  );
}

function emptyTitle(tab: BillingTransactionType): string {
  switch (tab) {
    case 'invoice':
      return 'No invoices yet.';
    case 'receipt':
      return 'No payments yet.';
    case 'creditnote':
      return 'No credit notes yet.';
    default:
      return 'No transactions yet.';
  }
}

/** Red gradient when the member owes money; green when settled / in credit. */
const GRADIENT_OWED = 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)';
const GRADIENT_SETTLED = 'linear-gradient(135deg, #047857 0%, #10b981 100%)';

export function BalanceCard({
  account,
  canPay,
}: {
  account: BillingAccount;
  /** True only when the tenant has a default payment channel + an amount is due. */
  canPay: boolean;
}): JSX.Element {
  const copy = pageCopy().invoices;
  const [paying, setPaying] = useState(false);
  // Positive balance == the member owes money. Anything at/below zero is
  // settled (a negative balance is a credit on the account).
  const owed = account.balance > 0.005;
  const inCredit = account.balance < -0.005;

  async function pay() {
    if (paying) return;
    setPaying(true);
    try {
      const url = await payAccountBalance(window.location.href);
      if (!url) return;
      if (platform.isNative()) {
        await Browser.open({ url, presentationStyle: 'popover' });
      } else {
        window.location.href = url;
      }
    } catch {
      /* surfaced by re-enabling the button */
    } finally {
      setPaying(false);
    }
  }

  const amountLabel = inCredit
    ? `${money(Math.abs(account.balance), account.currency_code)} ${copy.balanceCreditLabel}`
    : money(Math.max(0, account.balance), account.currency_code);

  return (
    <div
      className="overflow-hidden rounded-2xl p-5 text-white shadow-card"
      style={{ backgroundImage: owed ? GRADIENT_OWED : GRADIENT_SETTLED }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="eyebrow text-white/75">{copy.accountBalance}</span>
          <p className="mt-1 font-display text-display-lg tabular leading-none">{amountLabel}</p>
          <p className="mt-2 font-display text-headline leading-tight">
            {owed ? copy.balanceOwedTitle : copy.balanceSettledTitle}
          </p>
          <p className="mt-1 text-body-sm text-white/85">
            {owed ? copy.balanceOwedHint : copy.balanceSettledHint}
          </p>
        </div>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15">
          <Wallet className="h-5 w-5" strokeWidth={1.75} />
        </span>
      </div>

      {owed && canPay ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            pay();
          }}
          disabled={paying}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-pill bg-white px-4 py-2.5 font-display text-body font-semibold text-danger transition-transform duration-fast active:scale-[0.99] disabled:opacity-70"
        >
          {paying ? 'Starting payment…' : copy.payCta}
          {paying ? null : <ArrowRight className="h-4 w-4" strokeWidth={2} />}
        </button>
      ) : null}

      <div className="mt-3 flex items-center justify-between text-caption text-white/70">
        <span className="truncate">{account.account_code ?? ''}</span>
        {account.updated_at ? <span className="shrink-0">Updated {formatDate(account.updated_at)}</span> : null}
      </div>
    </div>
  );
}

/** Parse a date that may be 'YYYY-MM-DD' or a full ISO timestamp → local midnight. */
function parseDay(value: string): Date | null {
  if (!value) return null;
  const d = new Date(String(value).slice(0, 10) + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format a period as e.g. "1 Jun – 30 Jun 2026" (tolerates ISO timestamps). */
function periodLabel(from: string, to: string): string {
  const f = parseDay(from);
  const t = parseDay(to);
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  if (!f || !t) return `${String(from).slice(0, 10)} – ${String(to).slice(0, 10)}`;
  return `${f.toLocaleDateString(undefined, opts)} – ${t.toLocaleDateString(undefined, { ...opts, year: 'numeric' })}`;
}

/**
 * Statements section — lists the member's generated statements with a secure
 * PDF download. Rendered only when the financials:statements license is on.
 */
function StatementsCard({ currency }: { currency: string }): JSX.Element {
  const copy = pageCopy().invoices;
  const query = useQuery({ queryKey: ['member-statements'], queryFn: fetchStatements });
  const statements = query.data ?? [];
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function download(s: MemberStatement) {
    try {
      setDownloadingId(s.statement_id);
      const url = await fetchStatementDownloadUrl(s.statement_id);
      if (!url) return;
      // The URL is pre-signed — fetch it directly (no auth headers needed).
      const resp = await fetch(url);
      const blob = await resp.blob();
      await saveBlobAsDownload(blob, `statement_${s.period_from}_${s.period_to}.pdf`, 'Statement');
    } catch {
      /* surfaced via the disabled state resetting; no-op */
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Card noGrain className="mt-4 p-4">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-ink-secondary" strokeWidth={1.75} />
        <span className="font-display text-headline leading-tight text-ink">{copy.statementsTitle}</span>
      </div>

      {query.isLoading ? (
        <div className="mt-3 flex flex-col gap-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
        </div>
      ) : statements.length === 0 ? (
        <p className="mt-3 text-body-sm text-ink-muted">{copy.statementsHint}</p>
      ) : (
        <div className="mt-3 flex flex-col divide-y divide-[color:var(--hairline)]">
          {statements.map((s) => (
            <div key={s.statement_id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-body-sm text-ink">{periodLabel(s.period_from, s.period_to)}</p>
                <p className="text-caption text-ink-muted">
                  {money(s.closing_balance, s.currency_code || currency)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => download(s)}
                disabled={downloadingId === s.statement_id}
                aria-label={`Download statement ${periodLabel(s.period_from, s.period_to)}`}
                className="flex shrink-0 items-center gap-1.5 rounded-pill bg-primary-700 px-3 py-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-white disabled:opacity-50"
              >
                {downloadingId === s.statement_id ? (
                  <Clock className="h-3.5 w-3.5" strokeWidth={2} />
                ) : (
                  <Download className="h-3.5 w-3.5" strokeWidth={2} />
                )}
                PDF
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Arrears ageing summary — current / 30 / 60 / 90 / 120+ buckets. */
function AgeingSummaryCard({ currency }: { currency: string }): JSX.Element | null {
  const query = useQuery({ queryKey: ['member-ageing'], queryFn: fetchAgeingSummary });
  const ageing = query.data;
  if (!ageing || ageing.total_arrears <= 0.005) return null;

  const buckets: Array<{ label: string; value: number }> = [
    { label: 'Current', value: ageing.current },
    { label: '30 days', value: ageing.days_30 },
    { label: '60 days', value: ageing.days_60 },
    { label: '90 days', value: ageing.days_90 },
    { label: '120+ days', value: ageing.days_120_plus },
  ];
  const cur = ageing.currency_code || currency;

  return (
    <Card noGrain className="mt-4 p-4">
      <span className="font-display text-headline leading-tight text-ink">Account ageing</span>
      <div className="mt-3 flex flex-col divide-y divide-[color:var(--hairline)]">
        {buckets.map((b) => (
          <div key={b.label} className="flex items-center justify-between py-2">
            <span className="text-body-sm text-ink-secondary">{b.label}</span>
            <span className={'text-body-sm tabular-nums ' + (b.value > 0.005 ? 'font-semibold text-ink' : 'text-ink-muted')}>
              {money(b.value, cur)}
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between pt-2.5">
          <span className="text-body-sm font-semibold text-ink">Total arrears</span>
          <span className="text-body font-semibold tabular-nums text-ink">{money(ageing.total_arrears, cur)}</span>
        </div>
      </div>
    </Card>
  );
}

/**
 * Compact transaction row — title + status on one line, type · date as a
 * secondary line, and the amount (plus any outstanding/settled hint) on
 * the right. Replaces the previous tall 2-column detail grid.
 */
function TransactionCard({
  tx,
  accountCurrency,
}: {
  tx: BillingTransaction;
  accountCurrency: string;
}): JSX.Element {
  const currency = tx.currency_code || accountCurrency;
  const statusKey = (tx.status || '').toLowerCase();
  const statusTone = STATUS_TONE[statusKey] ?? 'bg-sunken text-ink-secondary';
  const isInvoice = tx.transaction_type === 'invoice';
  const hasDue = isInvoice && tx.amount_due > 0.005;
  const meta = [TYPE_LABEL[tx.transaction_type] ?? tx.transaction_type, formatDate(tx.created_at) || null]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card noGrain className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate font-display text-body font-semibold leading-tight text-ink">
              {docLabel(tx)}
            </p>
            {tx.status ? (
              <span
                className={cn(
                  'shrink-0 rounded-pill px-2 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-[0.04em]',
                  statusTone,
                )}
              >
                {tx.status}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 truncate text-caption text-ink-muted">
            {meta}
            {tx.description ? ` · ${tx.description}` : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-display text-headline leading-none tabular text-ink">
            {money(tx.total_in_vat, currency)}
          </p>
          {isInvoice ? (
            <p
              className={cn(
                'mt-1 text-caption tabular',
                hasDue ? 'font-semibold text-danger' : 'text-success',
              )}
            >
              {hasDue ? `${money(tx.amount_due, currency)} due` : 'Settled'}
            </p>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
